import axios from 'axios';
import { useAuth } from '@clerk/clerk-react';
import { useContext, useMemo } from 'react';
import { getApiConfig } from '../../app/services/environment';
import { UserContext } from '../../components/shared/useUser';
import {
  CreateNewsRequest,
  NewsAudiencePreview,
  NewsManagementFilters,
  NewsManagementItem,
  NewsManagementResult,
  NewsPreview,
  NewsTag,
  UpdateNewsRequest,
} from '../../app/services/api/news/newsManagementTypes';
import { NewsCommentsResult, NewsItemAnalytics } from '../../app/services/api/news/newsConsumerTypes';

interface SuccessResponse<T> {
  data: T;
}

export function useNewsManagementClient() {
  const { getToken } = useAuth();
  const { forceLogout } = useContext(UserContext);
  const baseUrl = getApiConfig().apiBaseUrl;

  return useMemo(() => {
    const client = axios.create({ baseURL: baseUrl });

    client.interceptors.request.use(async (config) => {
      const token = await getToken({ template: 'Orderly_default' });
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    client.interceptors.response.use(
      response => response,
      async error => {
        if (error?.response?.status === 401) {
          await forceLogout();
        }
        return Promise.reject(error);
      },
    );

    return {
      async getItems(filters: NewsManagementFilters): Promise<NewsManagementResult> {
        const response = await client.get<SuccessResponse<NewsManagementResult>>('/api/news-v2', {
          params: {
            tenantId: filters.tenantId,
            franchiseId: filters.franchiseId,
            status: filters.status,
            tagId: filters.tagId,
            isPinned: filters.isPinned,
            search: filters.search || undefined,
            page: filters.page ?? 1,
            pageSize: filters.pageSize ?? 25,
          },
        });
        return response.data.data;
      },

      async getItem(id: number): Promise<NewsManagementItem> {
        const response = await client.get<SuccessResponse<NewsManagementItem>>(`/api/news-v2/${id}`);
        return response.data.data;
      },

      async create(request: CreateNewsRequest): Promise<NewsManagementItem> {
        const response = await client.post<SuccessResponse<NewsManagementItem>>('/api/news-v2', request);
        return response.data.data;
      },

      async update(id: number, request: UpdateNewsRequest): Promise<NewsManagementItem> {
        const response = await client.put<SuccessResponse<NewsManagementItem>>(`/api/news-v2/${id}`, request);
        return response.data.data;
      },

      async publish(id: number, rowVersion: string, visibleFromUtc?: string | null, visibleUntilUtc?: string | null): Promise<NewsManagementItem> {
        const response = await client.post<SuccessResponse<NewsManagementItem>>(
          `/api/news-v2/${id}/publish`,
          { rowVersion, visibleFromUtc: visibleFromUtc || null, visibleUntilUtc: visibleUntilUtc || null },
        );
        return response.data.data;
      },

      async archive(id: number, rowVersion: string): Promise<NewsManagementItem> {
        const response = await client.post<SuccessResponse<NewsManagementItem>>(
          `/api/news-v2/${id}/archive`,
          { rowVersion },
        );
        return response.data.data;
      },

      async preview(contentHtml: string): Promise<NewsPreview> {
        const response = await client.post<SuccessResponse<NewsPreview>>('/api/news-v2/preview', { contentHtml });
        return response.data.data;
      },

      async getAudiencePreview(id: number): Promise<NewsAudiencePreview> {
        const response = await client.get<SuccessResponse<NewsAudiencePreview>>(`/api/news-v2/${id}/audience-preview`);
        return response.data.data;
      },

      async getTags(tenantId: number, activeOnly = true): Promise<NewsTag[]> {
        const response = await client.get<SuccessResponse<NewsTag[]>>('/api/news-v2/tags', {
          params: { tenantId, activeOnly },
        });
        return response.data.data;
      },

      async createTag(tenantId: number, name: string, description?: string, color?: string): Promise<NewsTag> {
        const response = await client.post<SuccessResponse<NewsTag>>('/api/news-v2/tags', {
          tenantId,
          name,
          description: description || null,
          color: color || null,
        });
        return response.data.data;
      },

      async updateTag(tag: NewsTag, name: string, description?: string, color?: string): Promise<NewsTag> {
        const response = await client.put<SuccessResponse<NewsTag>>(`/api/news-v2/tags/${tag.id}`, {
          name,
          description: description || null,
          color: color || null,
          rowVersion: tag.rowVersion,
        });
        return response.data.data;
      },

      async retireTag(tag: NewsTag): Promise<NewsTag> {
        const response = await client.post<SuccessResponse<NewsTag>>(
          `/api/news-v2/tags/${tag.id}/retire`,
          { rowVersion: tag.rowVersion },
        );
        return response.data.data;
      },

      async uploadHero(id: number, file: File, altText: string, rowVersion: string): Promise<NewsManagementItem> {
        const form = new FormData();
        form.append('file', file);
        form.append('altText', altText);
        form.append('rowVersion', rowVersion);
        const response = await client.post<SuccessResponse<NewsManagementItem>>(`/api/news-v2/${id}/hero`, form);
        return response.data.data;
      },

      async removeHero(id: number, rowVersion: string): Promise<NewsManagementItem> {
        const response = await client.delete<SuccessResponse<NewsManagementItem>>(`/api/news-v2/${id}/hero`, {
          params: { rowVersion },
        });
        return response.data.data;
      },

      async uploadAttachment(id: number, file: File, rowVersion: string, displayOrder = 0): Promise<NewsManagementItem> {
        const form = new FormData();
        form.append('file', file);
        form.append('rowVersion', rowVersion);
        form.append('displayOrder', String(displayOrder));
        const response = await client.post<SuccessResponse<NewsManagementItem>>(`/api/news-v2/${id}/attachments`, form);
        return response.data.data;
      },

      async removeAttachment(id: number, mediaId: number, rowVersion: string): Promise<NewsManagementItem> {
        const response = await client.delete<SuccessResponse<NewsManagementItem>>(
          `/api/news-v2/${id}/attachments/${mediaId}`,
          { params: { rowVersion } },
        );
        return response.data.data;
      },

      async getManagementComments(id: number): Promise<NewsCommentsResult> {
        const response = await client.get<SuccessResponse<NewsCommentsResult>>(`/api/news-v2/management/${id}/comments`);
        return response.data.data;
      },

      async moderateComment(id: number, commentId: number, reason: string, rowVersion: string): Promise<void> {
        await client.post(
          `/api/news-v2/management/${id}/comments/${commentId}/moderate`,
          { reason, rowVersion },
        );
      },

      async getAnalytics(id: number): Promise<NewsItemAnalytics> {
        const response = await client.get<SuccessResponse<NewsItemAnalytics>>(`/api/news-v2/management/${id}/analytics`);
        return response.data.data;
      },
    };
  }, [baseUrl, forceLogout, getToken]);
}
